/**
 * components/ValidationPanel/ValidationPanel.js
 * Orchestrateur pour les validations - Interface 2 colonnes
 */

import "./ValidationPanel.css";

import { ValidationPanelHorsService } from "./ValidationPanelHorsService.js";
import { ValidationPanelGain } from "./ValidationPanelGain.js";

import logger from "../../utils/logger.js";

export class ValidationPanel {
  constructor(state, options = {}) {
    this.state = state;
    this.options = {
      onValidationComplete: options.onValidationComplete || null,
      ...options,
    };

    this.element = null;
    this.isInitialized = false;

    // Sous-composants
    this.hsPanel = null;
    this.gainPanel = null;

    // Données consolidées
    this.validationData = {
      hsComments: [],
      gainConfirmations: [],
    };

    // etape initialization dans le store
    this.isDefaultInitialized = false;
  }

  /**
   * Initialiser les conflits detectés avec des valeurs par defaut
   */

  initializeDefaultResolutions() {
    // Initialiser fibres HS par défaut
    this.hsPanel.fibersHS.forEach((fiber) => {
      this.validationData.hsComments.push({
        reference: fiber.reference,
        port: fiber.port,
        comment: "Fibre hors service - localisation à préciser",
        type: "hs",
      });
    });

    // Initialiser gains par défaut
    this.gainPanel.suspiciousGains.forEach((gain) => {
      this.validationData.gainConfirmations.push({
        reference: gain.reference,
        port: gain.port,
        comment: "Fibre occupée avec mesures - statut à vérifier",
        type: "gain",
        confirmed: false,
      });
    });

    // Sauvegarder dans le store
    this.autoSave();
  }

  /**
   * Initialiser la validation
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Créer les sous-composants
      this.hsPanel = new ValidationPanelHorsService(this.state, {
        onCommentUpdated: (type, fiberReference, comment, port) => {
          this.handleHSCommentUpdated(fiberReference, comment, port);
        },
      });

      this.gainPanel = new ValidationPanelGain(this.state, {
        onGainConfirmed: (type, fiberReference, data) => {
          this.handleGainConfirmed(fiberReference, data);
        },
      });

      // Initialiser les sous-composants
      const hsResult = await this.hsPanel.initialize();
      const gainResult = await this.gainPanel.initialize();

      this.isInitialized = true;

      // Logique exclusive : restaurer OU initialiser par défaut
      if (!this.isDefaultInitialized) {
        const existingData = this.state.getSource("conflictResolutions");

        if (existingData?.status === "success" && existingData?.data?.fibers?.length > 0) {
          // Des données existent déjà, les restaurer
          this.restoreExistingData();
        } else {
          // Aucune donnée, créer les valeurs par défaut
          this.initializeDefaultResolutions();
        }

        this.isDefaultInitialized = true;
      }

      return {
        hsTotal: hsResult.totalFibers,
        gainTotal: gainResult.totalFibers,
        hasHSData: hsResult.hasData,
        hasGainData: gainResult.hasData,
        hasAnyData: hsResult.hasData || gainResult.hasData,
      };
    } catch (error) {
      logger.error(
        {
          context: "panel-initialization",
          error: error.message,
          stack: error.stack,
          isInitialized: this.isInitialized,
          hasHSPanel: !!this.hsPanel,
          hasGainPanel: !!this.gainPanel,
        },
        "Erreur initialisation ValidationPanel"
      );
      throw error;
    }
  }

  /**
   * Gestionnaire commentaire HS mis à jour
   */
  handleHSCommentUpdated(fiberReference, comment, port) {
    // Force port a int pas a string
    const portNumber = parseInt(port);

    const existingIndex = this.validationData.hsComments.findIndex(
      (item) => item.reference === fiberReference && item.port === portNumber
    );

    if (comment.trim().length > 0) {
      // Commentaire personnalisé par l'utilisateur
      const commentData = {
        reference: fiberReference,
        port: port,
        comment: comment.trim(),
        type: "hs",
      };

      if (existingIndex >= 0) {
        this.validationData.hsComments[existingIndex] = commentData;
      } else {
        this.validationData.hsComments.push(commentData);
      }
    } else {
      // Si commentaire vide, restaurer la valeur par défaut
      if (existingIndex >= 0) {
        this.validationData.hsComments[existingIndex] = {
          reference: fiberReference,
          port: port,
          comment: "Fibre hors service - localisation à préciser", // Valeur par défaut
          type: "hs",
        };
      }
    }

    this.autoSave();
  }

  /**
   * Gestionnaire gain confirmé
   */
  handleGainConfirmed(fiberReference, data) {
    // Force port a int pas string
    data.port = parseInt(data.port);
    const existingIndex = this.validationData.gainConfirmations.findIndex(
      (item) => item.reference === fiberReference && item.port === data.port
    );

    if (data.confirmed) {
      // Gain confirmé par l'utilisateur
      const confirmationData = {
        reference: fiberReference,
        comment: data.comment || "Gain confirmé - fibre reste occupée",
        port: data.port,
        type: "gain",
        confirmed: true,
      };

      if (existingIndex >= 0) {
        this.validationData.gainConfirmations[existingIndex] = confirmationData;
      } else {
        this.validationData.gainConfirmations.push(confirmationData);
      }
    } else {
      // Si décochée, restaurer la valeur par défaut
      if (existingIndex >= 0) {
        this.validationData.gainConfirmations[existingIndex] = {
          reference: fiberReference,
          comment: "Fibre occupée avec mesures - statut à vérifier", // Valeur par défaut
          port: data.port,
          type: "gain",
          confirmed: false,
        };
      }
    }

    this.autoSave();
  }

  /**
   * Sauvegarde automatique
   */
  autoSave() {
    const allValidations = [...this.validationData.hsComments, ...this.validationData.gainConfirmations];

    if (allValidations.length > 0) {
      // Construire les données au format attendu par le store
      const fibers = allValidations.map((validation) => this.buildFiberDataFromValidation(validation));

      // Sauvegarder dans le store
      this.state.setSourceSuccess("conflictResolutions", { fibers });

      // Message toaster discret
      this.state.addToasterMessage("success", `${fibers.length} validation(s) sauvegardée(s)`);
    }
  }

  /**
   * Construire les données de fibre à partir d'une validation
   */
  buildFiberDataFromValidation(validation) {
    const portMatch = validation.reference.match(/-(\d+)$/);
    const port = portMatch ? parseInt(portMatch[1]) : null;

    if (validation.type === "hs") {
      return {
        REFERENCE: validation.reference,
        ETAT: "hs",
        PORT: validation.port,
        COMMENT: validation.comment,
      };
    } else if (validation.type === "gain") {
      return {
        REFERENCE: validation.reference,
        ETAT: "occupee", // Reste occupée
        PORT: validation.port,
        COMMENT: validation.comment,
      };
    }

    return {
      REFERENCE: validation.reference,
      ETAT: "unknown",
      PORT: port,
      COMMENT: validation.comment || `Validation: ${validation.type}`,
    };
  }

  /**
   * Restaurer les données existantes depuis le store
   */
  restoreExistingData() {
    const existingData = this.state.getSource("conflictResolutions");
    if (existingData?.status === "success" && existingData?.data?.fibers) {
      existingData.data.fibers.forEach((fiber) => {
        if (fiber.ETAT === "hs") {
          // Synchroniser l'orchestrateur
          this.validationData.hsComments.push({
            reference: fiber.REFERENCE,
            port: parseInt(fiber.PORT),
            comment: fiber.COMMENT,
            type: "hs",
          });

          // Synchroniser l'interface SEULEMENT si ce n'est pas un message par défaut
          if (fiber.COMMENT !== "Fibre hors service - localisation à préciser") {
            const uniqueKey = `${fiber.REFERENCE}-${fiber.PORT}`;
            this.hsPanel.comments.set(uniqueKey, fiber.COMMENT);
          }
        } else if (fiber.ETAT === "occupee") {
          // Synchroniser l'orchestrateur
          this.validationData.gainConfirmations.push({
            reference: fiber.REFERENCE,
            port: parseInt(fiber.PORT),
            comment: fiber.COMMENT,
            type: "gain",
            confirmed: true,
          });

          // Synchroniser SEULEMENT si confirmé par l'utilisateur (pas valeur par défaut)
          if (fiber.COMMENT !== "Fibre occupée avec mesures - statut à vérifier") {
            const uniqueKey = `${fiber.REFERENCE}-${fiber.PORT}`;
            this.gainPanel.confirmations.set(uniqueKey, {
              confirmed: true,
              comment: fiber.COMMENT,
              port: parseInt(fiber.PORT),
            });
          }
        }
      });
    }
  }

  /**
   * Rendu principal
   */
  render() {
    if (!this.isInitialized) {
      return `
        <div class="validation-panel">
          <div class="validation-loading">
            <p>Initialisation de la validation...</p>
          </div>
        </div>
      `;
    }

    return `
      <div class="validation-panel">
        <div class="validation-header">
          ${this.renderStats()}
        </div>
        
        <div class="validation-content">
          <div class="validation-column validation-column-left">
            <div id="hs-panel-container">
              <!-- ValidationPanelHorsService sera monté ici -->
            </div>
          </div>
          
          <div class="validation-column validation-column-right">
            <div id="gain-panel-container">
              <!-- ValidationPanelGain sera monté ici -->
            </div>
          </div>
        </div>
        
      </div>
    `;
  }

  /**
   * Rendu des statistiques
   */
  renderStats() {
    if (!this.hsPanel || !this.gainPanel) return "";

    // Protection contre les méthodes inexistantes ou undefined
    const hsStats = this.hsPanel.getStats ? this.hsPanel.getStats() : { total: 0, completed: 0 };
    const gainStats = this.gainPanel.getStats ? this.gainPanel.getStats() : { total: 0, completed: 0 };

    // Valeurs par défaut si les stats sont undefined/null
    const hsTotal = hsStats?.total || 0;
    const hsCompleted = hsStats?.completed || 0;
    const gainTotal = gainStats?.total || 0;
    const gainCompleted = gainStats?.completed || 0;

    const totalItems = hsTotal + gainTotal;
    const completedItems = hsCompleted + gainCompleted;
    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    if (totalItems === 0) {
      return `
        <div class="validation-stats">
          <span class="stat-badge stat-success">Aucune validation requise</span>
        </div>
      `;
    }

    return `
      <div class="validation-stats">
        <span class="stat-item">
          <span class="stat-label">Fibres HS:</span>
          <span class="stat-value">${hsCompleted}/${hsTotal}</span>
        </span>
        <span class="stat-item">
          <span class="stat-label">Gains:</span>
          <span class="stat-value">${gainCompleted}/${gainTotal}</span>
        </span>
        <span class="stat-badge ${percentage === 100 ? "stat-success" : "stat-warning"}">
          ${percentage}% complété
        </span>
      </div>
    `;
  }

  /**
   * Rendu si aucune validation nécessaire
   */
  renderNoValidations() {
    return `
      <div class="validation-panel">
        <div class="no-validations">
          <div class="no-validations-icon">✅</div>
          <h3>Aucune validation requise</h3>
          <p>
            Aucune fibre hors service ni gain suspect détecté. 
            Vous pouvez procéder directement à la génération du rapport.
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Mettre à jour l'affichage
   */
  updateDisplay() {
    if (!this.element) return;

    // Mettre à jour les stats
    const statsContainer = this.element.querySelector(".validation-stats");
    if (statsContainer) {
      statsContainer.outerHTML = this.renderStats();
    }
  }

  /**
   * Monter le composant
   */
  async mount(container) {
    try {
      const initResult = await this.initialize();

      // Si aucune donnée, afficher l'état vide
      if (!initResult.hasAnyData) {
        this.element = document.createElement("div");
        this.element.innerHTML = this.renderNoValidations();
        const mountedElement = this.element.firstElementChild;

        if (typeof container === "string") {
          const targetContainer = document.querySelector(container);
          if (targetContainer) {
            targetContainer.appendChild(mountedElement);
          }
        } else if (container && container.appendChild) {
          container.appendChild(mountedElement);
        }

        this.element = mountedElement;
        return this.element;
      }

      // Rendu normal avec les 2 colonnes
      this.element = document.createElement("div");
      this.element.innerHTML = this.render();
      const mountedElement = this.element.firstElementChild;

      if (typeof container === "string") {
        const targetContainer = document.querySelector(container);
        if (targetContainer) {
          targetContainer.appendChild(mountedElement);
        } else {
          throw new Error(`Container not found: ${container}`);
        }
      } else if (container && container.appendChild) {
        container.appendChild(mountedElement);
      } else {
        throw new Error("Invalid container provided");
      }

      this.element = mountedElement;

      // Monter les sous-composants
      await this.mountSubComponents();

      return this.element;
    } catch (error) {
      logger.error(
        {
          context: "panel-mounting",
          error: error.message,
          stack: error.stack,
          containerType: typeof container,
          isInitialized: this.isInitialized,
          hasSubComponents: !!(this.hsPanel && this.gainPanel),
        },
        "Erreur montage ValidationPanel"
      );
      throw error;
    }
  }

  /**
   * Monter les sous-composants
   */
  async mountSubComponents() {
    // Monter ValidationPanelHorsService
    const hsContainer = this.element.querySelector("#hs-panel-container");
    if (hsContainer && this.hsPanel) {
      await this.hsPanel.mount(hsContainer);
    }

    // Monter ValidationPanelGain
    const gainContainer = this.element.querySelector("#gain-panel-container");
    if (gainContainer && this.gainPanel) {
      await this.gainPanel.mount(gainContainer);
    }

    // Écouter les changements pour mettre à jour l'affichage
    const updateStats = () => {
      setTimeout(() => this.updateDisplay(), 100);
    };

    // Surveiller les changements (polling simple)
    this.statsUpdateInterval = setInterval(updateStats, 2000);
  }

  /**
   * Démonter le composant
   */
  unmount() {
    // Arrêter l'interval de mise à jour
    if (this.statsUpdateInterval) {
      clearInterval(this.statsUpdateInterval);
      this.statsUpdateInterval = null;
    }

    // Démonter les sous-composants
    if (this.hsPanel) {
      this.hsPanel.unmount();
      this.hsPanel = null;
    }

    if (this.gainPanel) {
      this.gainPanel.unmount();
      this.gainPanel = null;
    }

    // Démonter le composant principal
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
